import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Input, Button, IconButton, Select, MenuItem } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Layout from '../../../../components/Layout';
import AdminNavbar from '../../../../components/Admin/AdminNavbar/AdminNavbar';
import TransactionRecordTable from '../../../../components/Admin/Organisation/TransactionRecordTable/TransactionRecordTable';
import { axiosInstance } from '../../../../utils/tokenUtils';
import FilterIcon from '../../../../assets/Admin/RequestImages/filter-icon.svg';
import ColoredFilterIcon from '../../../../assets/Admin/RequestImages/colored-filter-icon.svg';
import { styled } from '@mui/material/styles';
import usePagination from '@mui/material/usePagination';
import './TransactionRecords.css';
import TransactionRecordSkeleton from '../../../../components/SkeletonEffect/TransactionRecordSkeleton/TransactionRecordSkeleton';

const List = styled('ul')({
  listStyle: 'none',
  padding: 0,
  margin: '10px 0',
  display: 'flex',
  justifyContent: 'center',
});

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const TransactionRecords = () => {
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [transactions, setTransactions] = useState([]);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [datePickerAnchorEl, setDatePickerAnchorEl] = useState(null);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    count: 0,
  });

  // Date picker states
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateRange, setSelectedDateRange] = useState({ start: null, end: null });
  const [dateRangePreset, setDateRangePreset] = useState('week');

  // Temporary states for date picker (only applied when confirmed)
  const [tempSelectedDate, setTempSelectedDate] = useState(null);
  const [tempSelectedDateRange, setTempSelectedDateRange] = useState({ start: null, end: null });
  const [tempDateRangePreset, setTempDateRangePreset] = useState('week');

  const modalRef = useRef(null);
  const calendarRef = useRef(null);

  // Prevent duplicate initial fetch in React 18 Strict Mode
  const didInitialFetchRef = useRef(false);
  // Skip running date-range effect on initial mount
  const didMountDateRangeRef = useRef(false);
  // Deduplicate in-flight requests (and optionally cancel previous)
  const inFlightRef = useRef(false);
  const lastQueryKeyRef = useRef('');
  const abortControllerRef = useRef(null);
  // Ensure shimmer is visible for a minimum duration for better UX
  const minLoadingEndTimeRef = useRef(0);

  // Pagination helper for server-side pagination
  const { items } = usePagination({
    count: Math.ceil(pagination.count / pagination.limit) || 1,
    onChange: (event, value) => {
      fetchTransactionData(value, pagination.limit);
    },
  });

  const handleRowsPerPageChange = event => {
    const newLimit = event.target.value;
    if (newLimit !== pagination.limit) {
      fetchTransactionData(1, newLimit);
    }
  };

  // Handle clicking outside the calendar to close it
  useEffect(() => {
    const handleClickOutside = event => {
      if (openDatePicker && calendarRef.current && !calendarRef.current.contains(event.target)) {
        // Check if the click is not on the filter icon
        if (datePickerAnchorEl && !datePickerAnchorEl.contains(event.target)) {
          handleCancelDateRange();
        }
      }
    };

    const handleResize = () => {
      // Close calendar on window resize to prevent positioning issues
      if (openDatePicker) {
        handleCancelDateRange();
      }
    };

    if (openDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', handleResize);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
    };
  }, [openDatePicker, datePickerAnchorEl]);

  const fetchTransactionData = async (page = 1, limit = 20) => {
    try {
      const key = JSON.stringify({
        page,
        limit,
        start: selectedDateRange.start ? selectedDateRange.start.toISOString().split('T')[0] : '',
        end: selectedDateRange.end ? selectedDateRange.end.toISOString().split('T')[0] : '',
        search: debouncedSearchTerm || '',
      });

      if (inFlightRef.current && lastQueryKeyRef.current === key) {
        // Exact same request already in-flight; skip duplicate
        return;
      }

      inFlightRef.current = true;
      lastQueryKeyRef.current = key;

      const now = Date.now();
      const minDurationMs = 400;
      minLoadingEndTimeRef.current = now + minDurationMs;
      setLoading(true);
      setError(null);

      // Abort previous request if still in flight
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const params = new URLSearchParams({
        page: page.toString(),
        page_size: limit.toString(),
        transaction_date_sort: 'asc',
      });

      if (selectedDateRange.start && selectedDateRange.end) {
        params.append('start_date', selectedDateRange.start.toISOString().split('T')[0]);
        params.append('end_date', selectedDateRange.end.toISOString().split('T')[0]);
      }
      if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
        params.append('search_query', debouncedSearchTerm.trim());
      }

      const { data } = await axiosInstance.get(
        `/admin/organisation/transaction-history?${params.toString()}`,
        { signal: abortControllerRef.current.signal }
      );

      // Transform API response to match component expectations
      const transformedData = data.results.map(transaction => ({
        organizationName: transaction.organisation_name,
        transactionId: transaction.transaction_id,
        transactionDate: transaction.transaction_date,
        totalEarning: transaction.total_payout_amount,
        bankDeduction: transaction.bank_deduction_amount,
        actualAmount: transaction.actual_amount,
        country: transaction.country,
        ownerName: transaction.owner_name,
        ownerContactNumber: transaction.owner_phone_no,
        accountHolderName: transaction.account_holder_name,
        address: transaction.address,
        bankName: transaction.bank_name,
        ibanNumber: transaction.iban,
      }));

      setTransactions(transformedData);
      setPagination({
        page: data.page || page,
        limit: data.limit || limit,
        count: data.count,
      });
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        // Request was aborted; ignore
        return;
      }
      console.error('Error fetching transaction data:', error);
      let errorMessage = 'Failed to load transaction data. Please try again.';

      if (error.response?.status === 400) {
        errorMessage = 'Invalid search parameters. Please check your search criteria.';
      } else if (error.response?.status === 401) {
        errorMessage = 'You are not authorized to view this data.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Transaction data not found.';
      }

      setError(errorMessage);
      setTransactions([]);
    } finally {
      inFlightRef.current = false;
      const now = Date.now();
      const remaining = minLoadingEndTimeRef.current - now;
      if (remaining > 0) {
        setTimeout(() => {
          setLoading(false);
        }, remaining);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (didInitialFetchRef.current) return;
    didInitialFetchRef.current = true;
    fetchTransactionData();
  }, []);

  useEffect(() => {
    if (!didMountDateRangeRef.current) {
      didMountDateRangeRef.current = true;
      return;
    }
    fetchTransactionData(1, pagination.limit);
  }, [selectedDateRange]);

  const didMountSearchRef = useRef(false);
  useEffect(() => {
    if (!didMountSearchRef.current) {
      didMountSearchRef.current = true;
      return;
    }
    fetchTransactionData(1, pagination.limit);
  }, [debouncedSearchTerm]);

  // Download handler
  const handleDownload = async () => {
    try {
      const params = new URLSearchParams({
        doc_type: 'csv',
      });

      // Add date range parameters if selected
      if (selectedDateRange.start && selectedDateRange.end) {
        params.append('start_date', selectedDateRange.start.toISOString().split('T')[0]);
        params.append('end_date', selectedDateRange.end.toISOString().split('T')[0]);
      }

      const response = await axiosInstance.get(
        `/admin/organisation/transaction-history/download?${params.toString()}`
      );

      // Get the download URL from the response
      const downloadUrl = response.data;

      // Create a temporary link element to trigger the download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `transaction_history_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading transaction data:', error);
      setError('Failed to download transaction data. Please try again.');
    }
  };

  // Calendar helper functions
  const getDaysInMonth = date => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = date => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getMonthName = date => {
    return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  };

  const isToday = date => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = date => {
    if (!tempSelectedDate) return false;
    return date.toDateString() === tempSelectedDate.toDateString();
  };

  const isInRange = date => {
    if (!tempSelectedDateRange.start || !tempSelectedDateRange.end) return false;
    return date >= tempSelectedDateRange.start && date <= tempSelectedDateRange.end;
  };

  const isWeekend = date => {
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday or Saturday
  };

  const handleDateClick = date => {
    setTempSelectedDate(date);
    if (!tempSelectedDateRange.start) {
      setTempSelectedDateRange({ start: date, end: date });
    } else if (!tempSelectedDateRange.end || tempSelectedDateRange.start > date) {
      setTempSelectedDateRange({ start: date, end: tempSelectedDateRange.start });
    } else {
      setTempSelectedDateRange({ start: tempSelectedDateRange.start, end: date });
    }
  };

  const handlePresetClick = preset => {
    setTempDateRangePreset(preset);
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        start = weekStart;
        end = weekEnd;
        break;
      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case '6month':
        start = new Date(today.getFullYear(), today.getMonth() - 5, 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case '1year':
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date(today.getFullYear(), 11, 31);
        break;
      default:
        return;
    }

    setTempSelectedDateRange({ start, end });
    setTempSelectedDate(start);
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleConfirmDateRange = () => {
    // Apply the temporary changes to the actual state
    setSelectedDateRange(tempSelectedDateRange);
    setSelectedDate(tempSelectedDate);
    setDateRangePreset(tempDateRangePreset);

    // Close the date picker
    setOpenDatePicker(false);
    setDatePickerAnchorEl(null);

    // Refetch data with the new date range
    fetchTransactionData(1, pagination.limit);
  };

  const handleCancelDateRange = () => {
    // Reset temporary states to match current actual states
    setTempSelectedDateRange(selectedDateRange);
    setTempSelectedDate(selectedDate);
    setTempDateRangePreset(dateRangePreset);

    // Close the date picker
    setOpenDatePicker(false);
    setDatePickerAnchorEl(null);
  };

  const handleClickToOpenDatePicker = event => {
    // Always clear temporary states when opening the calendar
    setTempSelectedDate(null);
    setTempSelectedDateRange({ start: null, end: null });
    setTempDateRangePreset('week');
    setDatePickerAnchorEl(event.currentTarget);
    setOpenDatePicker(true);
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDayOfMonth = getFirstDayOfMonth(currentDate);
    const days = [];

    // Previous month days
    const prevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    const daysInPrevMonth = getDaysInMonth(prevMonth);
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      const day = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - 1,
        daysInPrevMonth - i
      );
      days.push({ date: day, isCurrentMonth: false });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const day = new Date(currentDate.getFullYear(), currentDate.getMonth(), i);
      days.push({ date: day, isCurrentMonth: true });
    }

    // Next month days
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      const day = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, i);
      days.push({ date: day, isCurrentMonth: false });
    }

    return days;
  };

  return (
    <Layout title="Klass Online | Transaction Records">
      <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
        <AdminNavbar />
      </Box>

      <Box className="h-[calc(100vh-64px)] overflow-y-auto relative bg-[#F9F9F9] rounded-lg pr-[10px]">
        <Box className="flex flex-col p-[1rem] gap-4">
          {!loading && error && (
            <Box
              sx={{
                backgroundColor: '#ffebee',
                color: '#c62828',
                padding: '12px',
                borderRadius: '4px',
                border: '1px solid #ffcdd2',
              }}
            >
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}

          {loading ? (
            <TransactionRecordSkeleton />
          ) : (
            <>
              <Box className="flex justify-between items-center flex-wrap gap-4">
                <Typography className="transaction-records-title">Transaction Records</Typography>
                <Box className="flex items-center gap-4 w-full sm:w-auto sm:min-w-[400px] max-w-full">
                  <Input
                    sx={{
                      textDecoration: 'none',
                      border: '1px solid var(--Stroke, #D5DEE3)',
                      borderRadius: '56px',
                      '&:before, &:after': {
                        borderBottom: 'none !important',
                      },
                      '&:hover:not(.Mui-disabled):before': {
                        borderBottom: 'none !important',
                      },
                      padding: '8px 10px',
                      '& .MuiInputBase-input': {
                        padding: '3px 5px 3px 5px',
                      },
                      background: '#FFF',
                      width: '100%',
                      height: '40px',
                      minWidth: '200px',
                    }}
                    type="search"
                    placeholder="Search by Organisation or Owner"
                    aria-label="Search"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    startAdornment={<SearchIcon className="search-icon-css" />}
                  />
                  {openDatePicker ? (
                    <img
                      src={ColoredFilterIcon}
                      alt="Filter"
                      style={{ width: '25px', height: '25px', cursor: 'pointer', flexShrink: 0 }}
                      onClick={handleClickToOpenDatePicker}
                    />
                  ) : (
                    <img
                      src={FilterIcon}
                      alt="Filter"
                      style={{ width: '25px', height: '25px', cursor: 'pointer', flexShrink: 0 }}
                      onClick={handleClickToOpenDatePicker}
                    />
                  )}
                  <button
                    type="button"
                    className={`download-btn-header ${transactions.length === 0 ? 'download-btn-disabled' : ''}`}
                    onClick={handleDownload}
                    disabled={transactions.length === 0}
                  >
                    <DownloadIcon
                      sx={{ color: transactions.length === 0 ? '#ccc' : '#fff', fontSize: 18 }}
                    />
                    <Typography
                      className={`download-btn-text ${transactions.length === 0 ? 'download-btn-text-disabled' : ''}`}
                    >
                      Download
                    </Typography>
                  </button>
                </Box>
              </Box>

              {/* Date Range Picker - Fixed Position */}
              {openDatePicker && (
                <Box
                  ref={calendarRef}
                  className="transaction-date-picker-fixed"
                  style={{
                    position: 'fixed',
                    top: datePickerAnchorEl
                      ? Math.min(
                          datePickerAnchorEl.getBoundingClientRect().bottom + 8,
                          window.innerHeight - 400
                        )
                      : 0,
                    left: datePickerAnchorEl
                      ? Math.max(
                          0,
                          Math.min(
                            datePickerAnchorEl.getBoundingClientRect().left,
                            window.innerWidth - 300
                          )
                        )
                      : 0,
                    zIndex: 1400,
                  }}
                >
                  <Box className="transaction-date-picker-container">
                    <Box className="transaction-date-picker-content">
                      {/* Date Range Presets */}
                      <Box className="transaction-date-presets">
                        <button
                          type="button"
                          className={`transaction-date-preset-btn ${tempDateRangePreset === 'week' ? 'transaction-date-preset-active' : ''}`}
                          onClick={() => handlePresetClick('week')}
                        >
                          This Week
                        </button>
                        <button
                          type="button"
                          className={`transaction-date-preset-btn ${tempDateRangePreset === 'month' ? 'transaction-date-preset-active' : ''}`}
                          onClick={() => handlePresetClick('month')}
                        >
                          This Month
                        </button>
                        <button
                          type="button"
                          className={`transaction-date-preset-btn ${tempDateRangePreset === '6month' ? 'transaction-date-preset-active' : ''}`}
                          onClick={() => handlePresetClick('6month')}
                        >
                          6 Month
                        </button>
                        <button
                          type="button"
                          className={`transaction-date-preset-btn ${tempDateRangePreset === '1year' ? 'transaction-date-preset-active' : ''}`}
                          onClick={() => handlePresetClick('1year')}
                        >
                          1 Year
                        </button>
                      </Box>

                      {/* Month Navigation */}
                      <Box className="transaction-month-navigation">
                        <IconButton
                          onClick={handlePreviousMonth}
                          className="transaction-month-nav-btn"
                        >
                          <ChevronLeftIcon />
                        </IconButton>
                        <Typography className="transaction-month-title">
                          {getMonthName(currentDate)}
                        </Typography>
                        <IconButton onClick={handleNextMonth} className="transaction-month-nav-btn">
                          <ChevronRightIcon />
                        </IconButton>
                      </Box>

                      {/* Calendar Grid */}
                      <Box className="transaction-calendar-grid">
                        {/* Day Headers */}
                        <Box className="transaction-calendar-header">
                          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                            <Box key={index} className="transaction-calendar-day-header">
                              {day}
                            </Box>
                          ))}
                        </Box>

                        {/* Calendar Days */}
                        <Box className="transaction-calendar-days">
                          {generateCalendarDays().map((dayObj, index) => {
                            const { date, isCurrentMonth } = dayObj;
                            const isWeekendDay = isWeekend(date);
                            const isTodayDate = isToday(date);
                            const isSelectedDate = isSelected(date);
                            const isInRangeDate = isInRange(date);

                            // Check if this is the start or end date of the range
                            const isRangeStart =
                              tempSelectedDateRange.start &&
                              date.toDateString() === tempSelectedDateRange.start.toDateString();
                            const isRangeEnd =
                              tempSelectedDateRange.end &&
                              date.toDateString() === tempSelectedDateRange.end.toDateString();
                            const isSingleSelection =
                              tempSelectedDateRange.start &&
                              tempSelectedDateRange.end &&
                              tempSelectedDateRange.start.toDateString() ===
                                tempSelectedDateRange.end.toDateString() &&
                              date.toDateString() === tempSelectedDateRange.start.toDateString();

                            let dayClassName = `transaction-calendar-day ${
                              !isCurrentMonth ? 'transaction-calendar-day-other-month' : ''
                            } ${isWeekendDay ? 'transaction-calendar-day-weekend' : ''} ${
                              isTodayDate ? 'transaction-calendar-day-today' : ''
                            }`;

                            // Apply range-specific styling
                            if (isSingleSelection) {
                              dayClassName += ' transaction-calendar-day-single-selection';
                            } else if (isRangeStart) {
                              dayClassName += ' transaction-calendar-day-range-start';
                            } else if (isRangeEnd) {
                              dayClassName += ' transaction-calendar-day-range-end';
                            } else if (isInRangeDate) {
                              dayClassName += ' transaction-calendar-day-in-range';
                            } else if (isSelectedDate) {
                              dayClassName += ' transaction-calendar-day-selected';
                            }

                            return (
                              <button
                                key={index}
                                type="button"
                                className={dayClassName}
                                onClick={() => handleDateClick(date)}
                                disabled={!isCurrentMonth}
                              >
                                {date.getDate()}
                              </button>
                            );
                          })}
                        </Box>
                      </Box>

                      {/* Action Buttons */}
                      <Box className="transaction-date-picker-actions">
                        <Button
                          variant="outlined"
                          onClick={handleCancelDateRange}
                          className="transaction-date-cancel-btn"
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleConfirmDateRange}
                          className="transaction-date-confirm-btn"
                        >
                          Confirm
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Active Date Range Display */}
              {/* {selectedDateRange.start && selectedDateRange.end && (
                <Box className="transaction-active-date-range">
                  <Typography className="transaction-active-date-text">
                    Date Range: {selectedDateRange.start.toLocaleDateString()} -{' '}
                    {selectedDateRange.end.toLocaleDateString()}
                  </Typography>
                </Box>
              )} */}

              <TransactionRecordTable data={transactions} />

              {/* Pagination Footer */}
              <Box className="trx-record-pagination-wrapper">
                <Box className="trx-record-pagination-info">
                  <Typography className="trx-record-pagination-text">Showing</Typography>
                  <Select
                    value={pagination.limit}
                    onChange={handleRowsPerPageChange}
                    className="trx-record-pagination-select"
                  >
                    {[10, 15, 20, 30].map(num => (
                      <MenuItem key={num} value={num}>
                        {num}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography className="trx-record-pagination-text">
                    of {pagination.count} records
                  </Typography>
                </Box>
                <nav>
                  <List>
                    {items.map(({ page: itemPage, type, selected, ...item }, index) => {
                      let children = null;
                      if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                        children = <span className="trx-record-pagination-ellipsis">…</span>;
                      } else if (type === 'page') {
                        children = (
                          <button
                            type="button"
                            className={`trx-record-pagination-btn ${selected ? 'trx-record-pagination-btn-active' : ''}`}
                            {...item}
                          >
                            {itemPage}
                          </button>
                        );
                      } else {
                        children = (
                          <button type="button" className="trx-record-pagination-btn" {...item}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </button>
                        );
                      }
                      return <li key={index}>{children}</li>;
                    })}
                  </List>
                </nav>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default TransactionRecords;
